import React, { useEffect, useState } from 'react';
import { Container, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Box, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { Edit, Delete, Visibility, ArrowBack, ListAlt } from '@mui/icons-material';
import api from '../../services/api';

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/shop/orders');
      setOrders(res.data); // Show all orders
    } catch (err) {
      setError('Failed to fetch orders');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleView = (order) => {
    setSelectedOrder(order);
    setViewOpen(true);
  };
  
  const handleCloseView = () => {
    setViewOpen(false);
    setSelectedOrder(null);
  };
  
  const handleDelete = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await api.delete(`/api/shop/orders/${orderId}`);
      fetchOrders();
    } catch (err) {
      setError('Failed to delete order');
    }
  };

  return (
    <Container maxWidth={false} disableGutters sx={{ py: 0, px: 0 }}>
      <Box display="flex" justifyContent="flex-start" mb={2}>
        <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="outlined" color="primary" sx={{ mb: 2 }}>
          Back
        </Button>
      </Box>
      <Box textAlign="center" mb={4}>
        <ListAlt sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" component="h1" gutterBottom>
          Karigar Order Directory
        </Typography>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          View and Manage All Orders
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 0 }}>
        <Typography variant="h4" gutterBottom sx={{ p: 2 }}>
          All Orders
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Bill Number</TableCell>
                  <TableCell>Order Date</TableCell>
                  <TableCell>Delivery Date</TableCell>
                  <TableCell>Required Date</TableCell>
                  <TableCell>Salesman</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map(order => (
                  <TableRow key={order._id} hover>
                    <TableCell>{order.billNumber}</TableCell>
                    <TableCell>{order.orderDate ? new Date(order.orderDate).toLocaleDateString() : ''}</TableCell>
                    <TableCell>{order.deliveryDate ? new Date(order.deliveryDate).toLocaleDateString() : ''}</TableCell>
                    <TableCell>{order.requiredDate ? new Date(order.requiredDate).toLocaleDateString() : ''}</TableCell>
                    <TableCell>{order.agentName}</TableCell>
                    <TableCell>{order.status}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleView(order)} color="primary">
                        <Visibility />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(order._id)} color="error">
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* View Order Dialog */}
      <Dialog open={viewOpen} onClose={handleCloseView} maxWidth="md" fullWidth>
        <DialogTitle>Order Details</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Bill Name:</strong> {selectedOrder.billName}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Bill Number:</strong> {selectedOrder.billNumber}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Customer Name:</strong> {selectedOrder.customer?.name}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Order Date:</strong> {selectedOrder.orderDate ? new Date(selectedOrder.orderDate).toLocaleDateString() : ''}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Delivery Date:</strong> {selectedOrder.deliveryDate ? new Date(selectedOrder.deliveryDate).toLocaleDateString() : ''}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Required Date:</strong> {selectedOrder.requiredDate ? new Date(selectedOrder.requiredDate).toLocaleDateString() : ''}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Agent Name:</strong> {selectedOrder.agentName}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Status:</strong> {selectedOrder.status}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Total Amount:</strong> {selectedOrder.totalAmount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Items:</strong>
              </Typography>
              <Box sx={{ ml: 2 }}>
                {selectedOrder.items?.map((item, idx) => (
                  <Typography key={idx} variant="body2" gutterBottom>
                    • {item.itemName} ({item.karat}) - Qty: {item.quantity}, Weight: {item.itemWeight}g
                    {item.karigarAbbreviation && `, Karigar: ${item.karigarAbbreviation}`}
                    {item.despatchStatus && ', Despatched'}
                    {item.workIssued && ', Work Issued'}
                    {item.workReceived && ', Work Received'}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseView}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrderList; 